import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { organizationApi } from '../api/organization';
import { auditLogger, AuditEventType, AuditSeverity } from '../services/auditLogger';
import type {
  Organization,
  UserOrganization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationRole,
} from '../api/types';

/**
 * Organization Store
 * Manages multi-tenant organization context and switching
 *
 * This store handles:
 * - Current active organization
 * - List of user's organizations
 * - Organization switching
 * - Organization CRUD operations
 * - User management within organizations
 */

interface OrganizationState {
  // State
  currentOrganization: Organization | null;
  userOrganizations: UserOrganization[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentOrganization: (organization: Organization | null) => void;
  switchOrganization: (organizationId: string) => Promise<void>;
  loadUserOrganizations: () => Promise<void>;
  createOrganization: (data: CreateOrganizationRequest) => Promise<Organization>;
  updateOrganization: (id: string, data: UpdateOrganizationRequest) => Promise<void>;
  deleteOrganization: (id: string) => Promise<void>;
  inviteUser: (organizationId: string, email: string, role: OrganizationRole) => Promise<void>;
  removeUser: (organizationId: string, userId: string) => Promise<void>;
  updateUserRole: (organizationId: string, userId: string, role: OrganizationRole) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      // Initial State
      currentOrganization: null,
      userOrganizations: [],
      isLoading: false,
      error: null,

      /**
       * Set the current organization without API call
       * Used when switching from local state
       */
      setCurrentOrganization: (organization) => {
        set({ currentOrganization: organization, error: null });
      },

      /**
       * Switch to a different organization
       * Updates context and reloads the page to fetch organization-specific data
       */
      switchOrganization: async (organizationId) => {
        const { currentOrganization, userOrganizations } = get();
        const previousOrg = currentOrganization;

        set({ isLoading: true, error: null });
        try {
          // Find the organization in user's list
          const userOrg = userOrganizations.find((uo) => uo.organization.id === organizationId);

          if (!userOrg) {
            auditLogger.log({
              eventType: AuditEventType.ORGANIZATION_SWITCH,
              severity: AuditSeverity.ERROR,
              result: 'denied',
              denialReason: 'Organization not found in user organizations',
              organizationId,
              action: 'switch_organization',
              metadata: {
                targetOrganizationId: organizationId,
                sourceOrganizationId: previousOrg?.id,
                sourceOrganizationName: previousOrg?.name,
              },
            });
            throw new Error('Organization not found in user organizations');
          }

          // Fetch fresh organization data
          // Backend returns { organization: Organization }
          const response = await organizationApi.getOrganization(organizationId);
          const organization = response.organization;

          // Log successful organization switch
          auditLogger.log({
            eventType: AuditEventType.ORGANIZATION_SWITCH,
            severity: AuditSeverity.INFO,
            result: 'allowed',
            organizationId: organization.id,
            organizationName: organization.name,
            userRole: userOrg.role,
            action: 'switch_organization',
            metadata: {
              sourceOrganizationId: previousOrg?.id,
              sourceOrganizationName: previousOrg?.name,
              targetOrganizationId: organization.id,
              targetOrganizationName: organization.name,
              userRoleInNewOrg: userOrg.role,
            },
          });

          set({ currentOrganization: organization, isLoading: false });

          // Reload page to fetch all data for new organization context
          // This ensures all components get fresh data scoped to the new organization
          window.location.reload();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to switch organization';

          auditLogger.log({
            eventType: AuditEventType.ORGANIZATION_SWITCH,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            organizationId,
            action: 'switch_organization',
            metadata: {
              targetOrganizationId: organizationId,
              sourceOrganizationId: previousOrg?.id,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Load all organizations the current user belongs to
       * Auto-selects first organization if none is currently selected
       */
      loadUserOrganizations: async () => {
        set({ isLoading: true, error: null });
        try {
          // Backend returns array of { organization, role, is_default }
          const response = await organizationApi.getUserOrganizations();

          // Convert to UserOrganization format
          const userOrganizations: UserOrganization[] = response.map((item) => ({
            user_id: '',
            organization_id: item.organization.id,
            organization: item.organization,
            role: (item.role || 'MEMBER') as OrganizationRole,
            joined_at: item.organization.created_at || new Date().toISOString(),
          }));

          set({ userOrganizations, isLoading: false });

          // Auto-select first organization if none is selected
          const { currentOrganization } = get();
          if (!currentOrganization && userOrganizations.length > 0) {
            set({ currentOrganization: userOrganizations[0].organization });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load organizations';
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Create a new organization
       * User becomes OWNER of the newly created organization
       * Auto-switches to the new organization
       */
      createOrganization: async (data) => {
        set({ isLoading: true, error: null });
        try {
          // Backend returns { organization_id: string, message: string }
          const response = await organizationApi.createOrganization(data);
          const organizationId = response.organization_id;

          // Log successful organization creation
          auditLogger.log({
            eventType: AuditEventType.ORGANIZATION_SWITCH,
            severity: AuditSeverity.INFO,
            result: 'allowed',
            organizationId,
            organizationName: data.name,
            userRole: 'OWNER',
            action: 'create_organization',
            resource: `/organizations/${organizationId}`,
            resourceType: 'organization',
            resourceId: organizationId,
            metadata: {
              organizationName: data.name,
              message: response.message,
            },
          });

          // Reload user's organizations list
          await get().loadUserOrganizations();

          // Fetch the newly created organization to get full details
          const orgResponse = await organizationApi.getOrganization(organizationId);
          const organization = orgResponse.organization;

          // Switch to the newly created organization
          set({ currentOrganization: organization, isLoading: false });

          return organization;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create organization';

          auditLogger.log({
            eventType: AuditEventType.SECURITY_VIOLATION,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            action: 'create_organization',
            metadata: {
              attemptedOrganizationName: data.name,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Update organization details
       * Refreshes the organization list if successful
       */
      updateOrganization: async (id, data) => {
        const { currentOrganization, userOrganizations } = get();
        const userOrg = userOrganizations.find((uo) => uo.organization.id === id);

        set({ isLoading: true, error: null });
        try {
          await organizationApi.updateOrganization(id, data);

          // Log successful organization update
          auditLogger.log({
            eventType: AuditEventType.ORGANIZATION_SWITCH,
            severity: AuditSeverity.INFO,
            result: 'allowed',
            organizationId: id,
            organizationName: currentOrganization?.name,
            userRole: userOrg?.role,
            action: 'update_organization',
            resource: `/organizations/${id}`,
            resourceType: 'organization',
            resourceId: id,
            metadata: {
              updates: data,
              organizationId: id,
            },
          });

          // Reload user's organizations list
          await get().loadUserOrganizations();

          // Update current organization if it was the one updated
          if (currentOrganization?.id === id) {
            // Backend returns { organization: Organization }
            const response = await organizationApi.getOrganization(id);
            set({ currentOrganization: response.organization });
          }

          set({ isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update organization';

          auditLogger.log({
            eventType: AuditEventType.SECURITY_VIOLATION,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            organizationId: id,
            action: 'update_organization',
            resource: `/organizations/${id}`,
            metadata: {
              attemptedUpdates: data,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Delete organization (soft delete)
       * Switches to another organization if deleting the current one
       */
      deleteOrganization: async (id) => {
        const { currentOrganization, userOrganizations } = get();
        const userOrg = userOrganizations.find((uo) => uo.organization.id === id);
        const orgToDelete = userOrg?.organization;

        set({ isLoading: true, error: null });
        try {
          await organizationApi.deleteOrganization(id);

          // Log successful organization deletion
          auditLogger.log({
            eventType: AuditEventType.SECURITY_VIOLATION,
            severity: AuditSeverity.WARNING,
            result: 'allowed',
            organizationId: id,
            organizationName: orgToDelete?.name,
            userRole: userOrg?.role,
            action: 'delete_organization',
            resource: `/organizations/${id}`,
            resourceType: 'organization',
            resourceId: id,
            metadata: {
              deletedOrganizationId: id,
              deletedOrganizationName: orgToDelete?.name,
              wasCurrentOrganization: currentOrganization?.id === id,
            },
          });

          // Reload organizations list
          await get().loadUserOrganizations();

          // If we deleted the current organization, switch to another
          const { userOrganizations: updatedOrgs } = get();
          if (currentOrganization?.id === id) {
            const nextOrg = updatedOrgs[0]?.organization || null;

            if (nextOrg) {
              auditLogger.log({
                eventType: AuditEventType.ORGANIZATION_SWITCH,
                severity: AuditSeverity.INFO,
                result: 'allowed',
                organizationId: nextOrg.id,
                organizationName: nextOrg.name,
                action: 'auto_switch_after_deletion',
                metadata: {
                  reason: 'Current organization was deleted',
                  deletedOrganizationId: id,
                  newOrganizationId: nextOrg.id,
                },
              });
            }

            set({ currentOrganization: nextOrg });
          }

          set({ isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete organization';

          auditLogger.log({
            eventType: AuditEventType.SECURITY_VIOLATION,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            organizationId: id,
            organizationName: orgToDelete?.name,
            action: 'delete_organization',
            resource: `/organizations/${id}`,
            metadata: {
              attemptedDeletionOfOrganizationId: id,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Invite a user to the organization
       * Sends email invitation with the specified role
       */
      inviteUser: async (organizationId, email, role) => {
        const { currentOrganization, userOrganizations } = get();
        const userOrg = userOrganizations.find((uo) => uo.organization.id === organizationId);

        set({ isLoading: true, error: null });
        try {
          await organizationApi.inviteUser(organizationId, { email, role });

          // Log successful user invitation
          auditLogger.log({
            eventType: AuditEventType.PERMISSION_GRANTED,
            severity: AuditSeverity.INFO,
            result: 'allowed',
            organizationId,
            organizationName: currentOrganization?.name,
            userRole: userOrg?.role,
            action: 'invite_user',
            resource: `/organizations/${organizationId}/users`,
            resourceType: 'organization_user',
            metadata: {
              invitedUserEmail: email,
              assignedRole: role,
              organizationId,
            },
          });

          set({ isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to invite user';

          auditLogger.log({
            eventType: AuditEventType.ACTION_BLOCKED,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            organizationId,
            action: 'invite_user',
            resource: `/organizations/${organizationId}/users`,
            metadata: {
              attemptedInviteEmail: email,
              attemptedRole: role,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Remove a user from the organization
       */
      removeUser: async (organizationId, userId) => {
        const { currentOrganization, userOrganizations } = get();
        const userOrg = userOrganizations.find((uo) => uo.organization.id === organizationId);

        set({ isLoading: true, error: null });
        try {
          await organizationApi.removeUser(organizationId, userId);

          // Log successful user removal
          auditLogger.log({
            eventType: AuditEventType.PERMISSION_DENIED,
            severity: AuditSeverity.WARNING,
            result: 'allowed',
            organizationId,
            organizationName: currentOrganization?.name,
            userRole: userOrg?.role,
            action: 'remove_user',
            resource: `/organizations/${organizationId}/users/${userId}`,
            resourceType: 'organization_user',
            resourceId: userId,
            metadata: {
              removedUserId: userId,
              organizationId,
            },
          });

          set({ isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to remove user';

          auditLogger.log({
            eventType: AuditEventType.ACTION_BLOCKED,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            organizationId,
            action: 'remove_user',
            resource: `/organizations/${organizationId}/users/${userId}`,
            metadata: {
              attemptedRemovalOfUserId: userId,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Update a user's role in the organization
       */
      updateUserRole: async (organizationId, userId, role) => {
        const { currentOrganization, userOrganizations } = get();
        const userOrg = userOrganizations.find((uo) => uo.organization.id === organizationId);

        set({ isLoading: true, error: null });
        try {
          await organizationApi.updateUserRole(organizationId, userId, role);

          // Log successful user role update
          auditLogger.log({
            eventType: AuditEventType.PERMISSION_GRANTED,
            severity: AuditSeverity.WARNING,
            result: 'allowed',
            organizationId,
            organizationName: currentOrganization?.name,
            userRole: userOrg?.role,
            action: 'update_user_role',
            resource: `/organizations/${organizationId}/users/${userId}`,
            resourceType: 'organization_user',
            resourceId: userId,
            metadata: {
              targetUserId: userId,
              newRole: role,
              organizationId,
            },
          });

          set({ isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update user role';

          auditLogger.log({
            eventType: AuditEventType.ACTION_BLOCKED,
            severity: AuditSeverity.ERROR,
            result: 'denied',
            denialReason: errorMessage,
            organizationId,
            action: 'update_user_role',
            resource: `/organizations/${organizationId}/users/${userId}`,
            metadata: {
              targetUserId: userId,
              attemptedRole: role,
              error: errorMessage,
            },
          });

          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      /**
       * Clear error message
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Reset the entire store to initial state
       * Used on logout
       */
      reset: () => {
        set({
          currentOrganization: null,
          userOrganizations: [],
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'organization-context',
      version: 1,
      // Only persist current organization, not the full list
      // Full list is fetched on login
      partialize: (state) => ({
        currentOrganization: state.currentOrganization,
      }),
      // Merge function to ensure proper state hydration
      merge: (persistedState, currentState) => {
        const persisted = persistedState as any;
        return {
          ...currentState,
          // Only restore currentOrganization from persisted state
          // userOrganizations should always be fetched fresh
          currentOrganization: persisted?.currentOrganization || null,
          userOrganizations: [], // Always initialize as empty array
        };
      },
    }
  )
);

export default useOrganizationStore;
